import React, { useEffect, useState } from "react";
import { api, weatherMeta, DAY_LABELS } from "../../lib/api";
import {
  Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning,
  Wind, Droplets, Loader2, CloudOff
} from "lucide-react";

const ICONS = { Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning };

export default function WeatherPanel({ lat, lng, days = 7 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let active = true;
    // Reset visual state whenever lat/lng/days changes so navigating from
    // campsite A → campsite B shows the loading spinner (instead of A's
    // temperature lingering on-screen until B's fetch resolves — that
    // stale-render made users think weather wasn't updating between sites).
    setLoading(true);
    setErr(null);
    setData(null);
    (async () => {
      try {
        const { data } = await api.get(`/weather?lat=${lat}&lng=${lng}&days=${days}`);
        if (active) setData(data);
      } catch (e) {
        if (active) setErr("Forecast unavailable");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [lat, lng, days]);

  if (loading) return (
    <div className="border hairline bg-white p-5 flex items-center gap-2 text-sm text-[#555]" data-testid="weather-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading forecast…
    </div>
  );
  if (err || !data) return (
    <div className="border hairline bg-white p-5 flex items-center gap-2 text-sm text-[#888]" data-testid="weather-error">
      <CloudOff className="w-4 h-4" /> {err || "No forecast available"}
    </div>
  );

  const cur = data.current || {};
  const meta = weatherMeta(cur.weather_code);
  const CurIcon = ICONS[meta.icon] || Cloud;

  const daily = data.daily || {};
  const times = daily.time || [];

  return (
    <div className="border hairline bg-white" data-testid="weather-panel">
      <div className="p-5 border-b hairline flex items-start justify-between gap-6">
        <div>
          <div className="eyebrow mb-2">Current Conditions</div>
          <div className="flex items-center gap-4">
            <CurIcon className="w-12 h-12 text-[#ff4500]" strokeWidth={1.25} />
            <div>
              <div className="font-display text-4xl mono leading-none" data-testid="weather-temp">
                {Math.round(cur.temperature_2m)}°<span className="text-lg text-[#888]">C</span>
              </div>
              <div className="text-sm text-[#555] mt-1">{meta.label}</div>
            </div>
          </div>
        </div>
        <div className="text-xs mono text-[#555] space-y-1.5 pt-1">
          <div className="flex items-center gap-1.5"><Wind className="w-3.5 h-3.5" strokeWidth={1.5} /> {Math.round(cur.wind_speed_10m)} mph</div>
          <div className="flex items-center gap-1.5"><Droplets className="w-3.5 h-3.5" strokeWidth={1.5} /> {cur.relative_humidity_2m}%</div>
        </div>
      </div>

      <div className="p-5">
        <div className="eyebrow mb-4">{(data.daily?.time || []).length}-Day Outlook</div>
        <div className={`grid gap-2 ${(data.daily?.time || []).length > 7 ? 'grid-cols-7 sm:grid-cols-7' : 'grid-cols-7'}`} data-testid="weather-forecast">
          {times.map((t, i) => {
            const code = daily.weather_code?.[i];
            const dMeta = weatherMeta(code);
            const Icon = ICONS[dMeta.icon] || Cloud;
            const d = new Date(t);
            const label = i === 0 ? "Today" : DAY_LABELS[d.getDay()];
            return (
              <div key={t} className="flex flex-col items-center text-center py-2" data-testid={`forecast-day-${i}`}>
                <div className="eyebrow mb-2">{label}</div>
                <Icon className="w-5 h-5 text-[#111]" strokeWidth={1.5} />
                <div className="mono text-xs mt-2">
                  <span className="font-semibold">{Math.round(daily.temperature_2m_max?.[i])}°</span>
                  <span className="text-[#888]"> / {Math.round(daily.temperature_2m_min?.[i])}°</span>
                </div>
                <div className="mono text-[10px] text-[#ff4500] mt-1">
                  {daily.precipitation_probability_max?.[i] ?? 0}%
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 text-[11px] mono text-[#888]">Source: Open-Meteo · Updated hourly</div>
      </div>
    </div>
  );
}
